// The chatbot's BRAIN files as the studio reads them (creative-code-studio-
// chatbot-prd §3.1): `bot.json` (name, greeting, starters) and `prompt.md` (the
// system prompt). The BACKEND is the validator (a bad write bounces with
// VFS_BOT_CONFIG_INVALID / VFS_BOT_PROMPT_REJECTED); this module only reads
// leniently — a half-typed bot.json must never crash the preview, it just
// renders the defaults until the kid finishes.

import type { VfsFile } from '../code/codeApi';
import type { SiteChatConfig } from './buildSitePreview';

export const BOT_CONFIG_PATH = 'bot.json';
export const BOT_PROMPT_PATH = 'prompt.md';

/** Mirrors the backend limits (`bot-config.ts` BOT_CONFIG_LIMITS + the 2,000-char prompt cap). */
export const BOT_LIMITS = {
  name: 40,
  greeting: 200,
  starters: 4,
  starter: 60,
  prompt: 2000,
} as const;

export const DEFAULT_BOT_NAME = 'My Bot';

export const DEFAULT_BOT_CONFIG: SiteChatConfig = {
  name: DEFAULT_BOT_NAME,
  greeting: '',
  starters: [],
};

/** Lenient parse of a `bot.json` body — never throws, fills defaults. */
export function parseBotConfig(raw: string | undefined | null): SiteChatConfig {
  if (!raw) return DEFAULT_BOT_CONFIG;
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return DEFAULT_BOT_CONFIG;
    const r = v as Record<string, unknown>;
    const name = typeof r.name === 'string' && r.name.trim() ? r.name.trim() : DEFAULT_BOT_NAME;
    const greeting = typeof r.greeting === 'string' ? r.greeting.trim() : '';
    const starters = Array.isArray(r.starters)
      ? r.starters.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
      : [];
    return {
      name: name.slice(0, BOT_LIMITS.name),
      greeting: greeting.slice(0, BOT_LIMITS.greeting),
      starters: starters.slice(0, BOT_LIMITS.starters).map((s) => s.trim().slice(0, BOT_LIMITS.starter)),
    };
  } catch {
    return DEFAULT_BOT_CONFIG;
  }
}

/** The bot config from a project VFS (`bot.json`), defaults when absent/invalid. */
export function readBotConfig(files: VfsFile[]): SiteChatConfig {
  const file = files.find((f) => f.kind === 'text' && f.path === BOT_CONFIG_PATH);
  return parseBotConfig(file?.content);
}

/** The system prompt from a project VFS (`prompt.md`), '' when absent. */
export function readBotPrompt(files: VfsFile[]): string {
  return files.find((f) => f.kind === 'text' && f.path === BOT_PROMPT_PATH)?.content ?? '';
}

/** Serialise a config back to the pretty `bot.json` text the kid can read. */
export function serializeBotConfig(config: SiteChatConfig): string {
  return `${JSON.stringify(
    { name: config.name, greeting: config.greeting, starters: config.starters },
    null,
    2,
  )}\n`;
}
